import GameLoop from "./GameLoop";
import GameStateControl from "./GameStateControl.js";
import InputHandler from "./input/InputHandler";
import SkillTreeInputHandler from "./input/SkillTreeInputHandler";
import UserInput from "./input/UserInput";
import State from "./State";
import GameView from "./view/GameView";

export default class GameState extends GameStateControl {
    constructor() {
        super();

        /**
         * @constant
         * @type {State}
         */
        this.state = new State(this);

        /**
         * @constant
         * @type {UserInput}
         */
        this.mineCanvasUserInput = new UserInput();

        /**
         * @constant
         * @type {UserInput}
         */
        this.skillTreeUserInput = new UserInput();

        /**
         * @constant
         * @type {GameView}
         */
        this.gameView = new GameView(this, this.mineCanvasUserInput, this.skillTreeUserInput);

        /**
         * The main Canvas input handler.
         * @constant
         * @type {InputHandler}
         */
        this.inputHandler = new InputHandler(this.mineCanvasUserInput, this.state.projection, this.state.worldGrid, this.state.panAnimation);

        /**
         * @constant
         * @type {SkillTreeInputHandler}
         */
        this.skillTreeInputHandler = new SkillTreeInputHandler(this.skillTreeUserInput, this.state.skillTreeGraph);

        /**
         * @constant
         * @type {GameLoop}
         */
        this.gameLoop = new GameLoop(this.state, this.gameView, this.inputHandler, this.skillTreeInputHandler);

        /**
         * @type {boolean}
         */
        this.started = false;

        /**
         * @type {boolean}
         */
        this.gameInitialized = false;

        /**
         * @type {string}
         */
        this.saveStateText = null;
    }

    /**
     * @param {boolean} gameVisible 
     */
    onVisibilityChange(gameVisible) {
        this.state.gameVisible = gameVisible;
    }

    /**
     * Invoked whenever the window is resized.
     * @param {number} width 
     * @param {number} height 
     */
    onWindowResize(width, height) {
        const canvas = this.gameView.getCanvas();
        if (canvas) {
            canvas.width = width;
            canvas.height = height;
        }
    }

    saveGame() {
        this.state.saveManager.saveGame(this.state);
    }

    /**
     * @override
     * @returns {State}
     */
    getState() {
        return this.state; 
    }

    /**
     * @returns {boolean}
     */
    isStarted() {
        return this.started;
    }

    /**
     * @override
     */
    startGame() {
        if (this.started) {
            return;
        }

        // Make sure we initiale the game before we start the game loop.
        if (!this.gameInitialized) {
            // Initialization Steps:
            // 1. Load the save
            // 2. Initialize some game models that need the save loaded before they can be setup.
            // 3. Intantiate the view
            this.initialize();
        }

        this.gameLoop.startGameLoop();
        this.started = true;
    }

    /**
     * Perform one-time initialization.
     * The sprites must be loaded before this is invoked.
     * This must be invoked before gameLoop().
     * @private
     */
    initialize() {
        if (this.gameInitialized) {
            return;
        }

        if (!this.state.sprites.isInitialized()) {
            console.log('GameState.initialize() ERROR The sprites have not been initialed yet.')
        }

        // Load the save data
        const saveLoaded = this.state.saveManager.loadSave(this.state);

        // Initialize the state once we're done loading the save file.
        this.state.initializeState(saveLoaded);

        // Invoke the one-time-only 'create children' method that will instantiate all game views.
        this.gameView.createChildren(this.state);

        // Make sure we don't enter this block of code again.
        this.gameInitialized = true;
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    prestigeGame() {
        this.gameLoop.requestGameStateCallback(this, this.CALLBACK_GAME_PRESTIGE);
    }

    /**
     * @override
     */
    resetGame() {
        this.gameLoop.requestGameStateCallback(this, this.CALLBACK_GAME_RESET);
    }

    /**
     * @override
     */
    deleteGame() {        
        this.gameLoop.requestGameStateCallback(this, this.CALLBACK_GAME_DELETE);
    }

    /**
     * @override
     * @param {string} saveStateText 
     */
    importSave(saveStateText) {
        this.saveStateText = saveStateText;
        this.gameLoop.requestGameStateCallback(this, this.CALLBACK_GAME_IMPORT);
    }

    /**
     * @abstract
     * @param {number} callbackCode 
     */
    modelLoadCallback(callbackCode) {
        if (callbackCode === this.CALLBACK_GAME_RESET) {
            this.resetInternal();
        }
        else if (callbackCode === this.CALLBACK_GAME_DELETE) {
            this.deleteInternal();
        }
        else if (callbackCode === this.CALLBACK_GAME_IMPORT) {
            this.importInternal();
        }
        else if (callbackCode === this.CALLBACK_GAME_PRESTIGE) {
            this.prestigeInternal();
        }
        else {
            console.log('GameState.modelLoadCallback(callbackCode) Invalid callback code: ' + callbackCode);
        }
    }

    /**
     * @private
     */
    resetInternal() {
        const saveManager = this.state.saveManager;

        // Delete the save and all the grid saves.
        saveManager.deleteSave();

        // Reset the game models.
        this.state.resetForPrestige();

        // Clear and Rebuild the UI.
        this.gameView.resetGameView();
        this.gameView.createChildren(this.state);

        saveManager.saveGame(this.state);
    }

    /**
     * @private
     */
    deleteInternal() {
        const saveManager = this.state.saveManager;

        // Delete the save and all the grid saves.
        saveManager.deleteSave();

        // Reset the game models.
        this.state.resetFull();

        // Clear and Rebuild the UI.
        this.gameView.resetGameView();
        this.gameView.createChildren(this.state);

        saveManager.saveGame(this.state);
    }

    /**
     * @private
     */
    prestigeInternal() {
        // Invoke Totals.onPrestige() and then just reset the game.

        // Add the unclaimed prestige points from totals to the stats.
        this.state.statisticsIncrementor.incrementPrestigePoints(this.state.totals.unclaimedPrestige);

        this.state.totals.onPrestige();
        this.resetInternal();
    }

    /**
     * @private
     */
    importInternal() {
        console.log('GameState.importInternal() START');

        const saveManager = this.state.saveManager;

        // Get a copy of the current state, just in case the import is invalid.
        const oldSaveStateText = saveManager.getSaveStateForExport(this.state);

        // We need to reset the game models before we import the save.
        this.state.beforeImportSave();

        if (!saveManager.importSave(this.state, this.saveStateText)) {
            // If the import failed, then we need to import the current save instead.
            
            console.log('GameState.importInternal() IMPORT FAILED. REVERTING TO PREV SAVE');

            // First we will need to reset the game models again, since our failed import will have modified them.
            this.state.beforeImportSave();

            saveManager.importSave(this.state, oldSaveStateText);
        }

        // We can clear out the save state text now.
        this.saveStateText = null;

        this.state.afterImportSave();

        this.gameView.resetGameView();
        this.gameView.createChildren(this.state);

        console.log('GameState.importInternal() DONE');
    }

}